import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Plane, Sparkles, ConciergeBell, Building2, Wrench, ShieldCheck } from "lucide-react";

const features = [
  { icon: Plane, title: "Transfer & Vale", desc: "Havalimanı transferi ve vale hizmetleri" },
  { icon: Sparkles, title: "Temizlik & Bakım", desc: "Profesyonel temizlik, çamaşır ve housekeeping" },
  { icon: ConciergeBell, title: "Concierge", desc: "Rezervasyon ve concierge hizmetleri" },
  { icon: Building2, title: "Sosyal Alanlar", desc: "Yaşam alanları ve sosyal mekân yönetimi" },
  { icon: Wrench, title: "Teknik Servis", desc: "Sürekli kalite kontrol ve teknik destek" },
  { icon: ShieldCheck, title: "Merkezi Yönetim", desc: "Tek merkezden profesyonel işletme" },
];

export default function LivingInvestment() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="investment"
      data-testid="investment-section"
      ref={ref}
      className="py-24 md:py-32 lg:py-40 bg-[#1A1A1A] relative overflow-hidden"
    >
      {/* Subtle pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="w-full h-full" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, #d5bd9a 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="flex items-center gap-4 mb-6"
        >
          <div className="w-8 h-[2px] bg-[#c13a05]" />
          <span className="font-sub text-[#c13a05] text-[11px] tracking-[0.25em] font-bold">
            YAŞAM YATIRIM MODELİ
          </span>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="md:col-span-6"
          >
            <h2 className="font-heading text-4xl sm:text-5xl font-normal text-white tracking-tight leading-tight mb-4">
              Konut Değil,<br />
              <span className="italic text-[#d5bd9a]">İşletilen Yaşam</span>
            </h2>
            {/* Logo in white */}
            <img src="/images/logos/logo-beyaz.svg" alt="FINORA" className="h-6 opacity-30 mt-4" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="md:col-span-6 flex items-end"
          >
            <p className="font-body text-white/55 text-base leading-relaxed font-light">
              FINORA yalnızca yaşanan bir yer değil, profesyonel olarak işletilen bir yaşam varlığıdır.
              Tüm konutlar tek merkezden yönetilen bir kiralama sistemine dahil edilir.
            </p>
          </motion.div>
        </div>

        {/* Highlight bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="border border-[#d5bd9a]/25 p-8 md:p-10 mb-16"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {[
              { label: "Kullanım", desc: "Kendi yaşam alanınız", color: "#c13a05" },
              { label: "Getiri", desc: "Düzenli kira geliri", color: "#7d7441" },
              { label: "İşletme", desc: "Profesyonel yönetim", color: "#9ac2cc" },
            ].map((item, i) => (
              <div key={i} className={i < 2 ? "md:border-r md:border-[#d5bd9a]/15" : ""}>
                <div className="w-2 h-2 rounded-full mx-auto mb-3" style={{ backgroundColor: item.color }} />
                <p className="font-heading text-2xl text-white font-normal mb-2">{item.label}</p>
                <p className="font-body text-sm text-white/40 font-light">{item.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Features grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((feature, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 + i * 0.08 }}
              data-testid={`feature-${i}`}
              className="border border-white/8 p-6 md:p-8 hover:border-[#d5bd9a]/30 transition-all duration-500 group"
            >
              <feature.icon className="w-5 h-5 text-[#d5bd9a] mb-5 group-hover:text-[#c13a05] transition-colors duration-300" strokeWidth={1.5} />
              <p className="font-sub text-sm text-white font-semibold mb-2">{feature.title}</p>
              <p className="font-body text-sm text-white/40 font-light leading-relaxed">{feature.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Bottom quote */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-16 pt-10 border-t border-white/8 text-center"
        >
          <p className="font-heading text-xl sm:text-2xl text-white/70 font-normal italic max-w-2xl mx-auto">
            "FINORA'da mülk sahibi olmak, bir işletmeye ortak olmak anlamına gelir."
          </p>
        </motion.div>
      </div>
    </section>
  );
}
